import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';
import { MemberIcon } from '@trello/nachos/icons/member';
import { token } from '@trello/theme';
import { learnMoreUrl } from '@trello/workspaces/user-limit';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import {
  useOpenPlanSelectionCallback,
  WithEnterpriseManagedOverride,
} from 'app/src/components/UpgradePrompts';

import styles from './CollaboratorsBadgeTooltipContent.less';

export interface CollaboratorsBadgeTooltipContentProps {
  workspaceId: string;
  onCtaClick?: () => void;
}

export const CollaboratorsBadgeTooltipContent: FunctionComponent<
  CollaboratorsBadgeTooltipContentProps
> = ({ workspaceId, onCtaClick }) => {
  const { isEligible } = useFreeTrialEligibilityRules(workspaceId, {
    skip: !workspaceId,
  });
  const openPlanSelection = useOpenPlanSelectionCallback(workspaceId, {
    redirect: false,
  });

  const onClickCta = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: isEligible ? 'startFreeTrialLink' : 'upgradeLink',
      source: 'collaboratorsBadgeTooltip',
    });
    openPlanSelection();
    onCtaClick?.();
  }, [openPlanSelection, onCtaClick, isEligible]);

  const onClickLearnMore = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'learnMoreLink',
      source: 'collaboratorsBadgeTooltip',
    });
    onCtaClick?.();
  }, [onCtaClick]);

  const tooltipAction = useMemo(() => {
    if (isEligible) {
      return (
        <a href="#" onClick={onClickCta}>
          <WithEnterpriseManagedOverride>
            <FormattedMessage
              id="templates.org_members.start-free-trial"
              defaultMessage="Start free trial"
              description="Start free trial link"
            />
          </WithEnterpriseManagedOverride>
        </a>
      );
    } else if (dontUpsell()) {
      return (
        <a href={learnMoreUrl} target="_blank" onClick={onClickLearnMore}>
          <FormattedMessage
            id="templates.org_members.learn-more"
            defaultMessage="Learn more"
            description="Learn more link"
          />
        </a>
      );
    } else {
      return (
        <a href="#" onClick={onClickCta}>
          <WithEnterpriseManagedOverride>
            <FormattedMessage
              id="templates.org_members.upgrade"
              defaultMessage="Upgrade"
              description="Upgrade link"
            />
          </WithEnterpriseManagedOverride>
        </a>
      );
    }
  }, [isEligible, onClickCta, onClickLearnMore]);

  return (
    <div className={styles.tooltipContent}>
      <h3>
        {dontUpsell() ? (
          <FormattedMessage
            id="templates.org_members.get-unlimited-collaborators"
            defaultMessage="Get unlimited collaborators"
            description="Get unlimited collaborators"
          />
        ) : (
          <FormattedMessage
            id="templates.org_members.upgrade-for-unlimited-collaborators"
            defaultMessage="Upgrade for unlimited collaborators"
            description="Upgrade for unlimited collaborators"
          />
        )}
      </h3>
      <p>
        <FormattedMessage
          id="templates.org_members.free-workspaces-collaborator-limit"
          defaultMessage="Free Workspaces can have up to 10 members, guests, and pending invitations."
          description="Free Workspaces can have up to 10 members, guests, and pending invitations."
        />
      </p>
      <div className={styles.ctaContainer}>{tooltipAction}</div>
      <div className={styles.iconContainer}>
        <MemberIcon
          size="large"
          color={token('color.icon.inverse', '#FFFFFF')}
        />
      </div>
      <div className={styles.iconBackground} />
    </div>
  );
};
